import tokenService from '@/utils/tokenService';

import { THREAD_SEARCH_ENDPOINT } from '@/lib/constants';

export type ThreadSearchItem = {
  id: string;
  name: string | null;
  createdAt: string;
  snippet: string | null;
};

export type ThreadSearchResponse = {
  data: ThreadSearchItem[];
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
};

export class ThreadSearchAuthError extends Error {
  status: 401 | 403;
  constructor(status: 401 | 403, message?: string) {
    super(message || `Auth error (${status})`);
    this.name = 'ThreadSearchAuthError';
    this.status = status;
  }
}

type SearchThreadsOptions = {
  limit?: number;
  includeSnippet?: boolean;
};

function normalizeQuery(q: string) {
  return q.trim().replace(/\s+/g, ' ');
}

export async function searchThreads(
  q: string,
  { limit = 20, includeSnippet = true }: SearchThreadsOptions = {}
): Promise<ThreadSearchItem[]> {
  const normalized = normalizeQuery(q);
  if (!normalized) return [];

  const token = await tokenService.getValidToken();
  // Authentication is mandatory: never call the endpoint without a token.
  if (!token) return [];

  const cappedLimit = Math.max(1, Math.min(50, limit));
  const url = `${THREAD_SEARCH_ENDPOINT}?q=${encodeURIComponent(
    normalized
  )}&limit=${cappedLimit}&include_snippet=${includeSnippet ? 'true' : 'false'}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });

  if (res.status === 401 || res.status === 403) {
    throw new ThreadSearchAuthError(res.status);
  }

  const data = (await res.json()) as ThreadSearchResponse | { detail?: string };

  if (!res.ok) {
    const message =
      typeof (data as any)?.detail === 'string'
        ? (data as any).detail
        : `HTTP error! status: ${res.status}`;
    throw new Error(message);
  }

  return (data as ThreadSearchResponse).data || [];
}

